// @flow
import { Box, Card, InputAdornment, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Grid from '@mui/material/Grid2';
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from 'react';
import * as yup from 'yup'
import { FormikProvider, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import UsernameIcon from "../../assets/images/icons/login/usernameIcon";
import PasswordInput from "../../components/form/passwordInput/passwordInput";
import Logo from "../../components/layout/logo/logo";
import { textFieldDefaults } from "../../helpers/theme/defaultProps";
import { PAGE_LOGIN } from "../../locales/pages/namespaces";
import useUserAction from "../../hooks/user/useUserAction";
import useUserData from "../../hooks/user/useUserData";

const Login = () => {
	const { t } = useTranslation(PAGE_LOGIN)
	const { loading, message } = useUserData()
	const { userLogin, checkAPIConnectivity, setUserMessage } = useUserAction()
	const theme = useTheme();
	
	useEffect(() => {
		checkAPIConnectivity()
	}, [checkAPIConnectivity])
	
	useEffect(() => {
		if (message) {
			enqueueSnackbar(t(message?.text), {
					variant: message?.variant,
					autoHideDuration: null
				}
			)
			setUserMessage(null)
		}
	}, [message, setUserMessage, t])
	
	const onSubmit = (values) => {
		userLogin(values)
	}
	
	const validationSchema = yup.object({
		username: yup
		.string()
		.required(t('required')),
		password: yup
		.string()
		.required(t('required')),
	})
	
	const formik = useFormik({
		initialValues: {
			username: '',
			password: '',
			rememberMe: false,
			email: '',
		},
		validationSchema: validationSchema,
		onSubmit: onSubmit,
	})
	
	return (
		<Box sx={{ mt: 8, marginTop: '0px' }}>
			<FormikProvider value={formik}>
				<Card
					elevation={0}
					sx={{
						background: '#FFFFFF',
						borderRadius: '12px',
						padding: '48px 34px',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							paddingBottom: '48px'
						}}
					>
						<Logo/>
					</Box>
					<Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 1, maxWidth: '440px' }}>
						<Grid container spacing={4}>
							<Grid size={12}>
								<TextField
									{...textFieldDefaults}
									required
									id={'username'}
									name={'username'}
									placeholder={t('Username')}
									autoComplete={'username'}
									value={formik.values.username}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									sx={{
										"& input::placeholder": {
											color: theme.palette.primary[800],
											opacity: 1,
										},
									}}
									slotProps={{
										input: {
											endAdornment: (
												<InputAdornment
													position="end"
												>
													<UsernameIcon/>
												</InputAdornment>
											)
										},
									}}
								/>
							</Grid>
							<Grid size={12}>
								<PasswordInput
									required
									name={'password'}
									value={formik.values.password}
									onChange={formik.handleChange}
									onBlur={formik.handleBlur}
									placeholder={t('Enter Password')}
									allowBrowserAutocomplete
									sx={{
										"& input::placeholder": {
											color: theme.palette.primary[800],
											opacity: 1,
										},
									}}
								/>
							</Grid>
						</Grid>
						<Button
							loading={loading}
							type="submit"
							size={"large"}
							fullWidth
							color={'highlight'}
							variant="contained"
							sx={{ mt: 5, height: '50px' }}
						>
							{t('Login')}
						</Button>
					</Box>
				</Card>
			</FormikProvider>
		</Box>
	)
}

export default Login